import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MessageSquare, Send, MapPin, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";

export default function Contact() {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate sending (you can integrate with a real backend endpoint)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success("Message sent! We'll get back to you soon.");
    setFormData({ name: "", email: "", subject: "", message: "" });
    setSending(false);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#050505]' : 'bg-slate-100'} transition-colors duration-300`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/">
            <Button variant="ghost" size="sm" className="rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div className="glass rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <MessageSquare className={`w-8 h-8 ${isDarkMode ? 'text-[#00f2ea]' : 'text-cyan-600'}`} />
              <h1 className="text-3xl font-bold">Contact Us</h1>
            </div>
            
            <p className="opacity-70 mb-8 leading-relaxed">
              Have questions, feedback, or need support? We'd love to hear from you! Fill out the form or reach out directly through one of the channels below.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Email</h3>
                  <p className="opacity-70">support@theforecasttracker.com</p>
                  <p className="opacity-70">press@theforecasttracker.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Location</h3>
                  <p className="opacity-70">Nashville, Tennessee</p>
                  <p className="opacity-70">United States</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Response Time</h3>
                  <p className="opacity-70">We typically respond within 24-48 hours</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass rounded-2xl p-8">
            <h2 className="text-xl font-semibold mb-6">Send a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 opacity-70">Your Name</label>
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 opacity-70">Email Address</label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 opacity-70">Subject</label>
                <Input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="How can we help?"
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 opacity-70">Message</label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us more..."
                  rows={5}
                  className={`w-full rounded-xl p-3 resize-none border transition-colors
                    ${isDarkMode 
                      ? 'bg-white/5 border-white/10 focus:border-[#00f2ea]/50' 
                      : 'bg-white border-black/10 focus:border-cyan-500/50'
                    }
                    focus:outline-none focus:ring-2 focus:ring-cyan-500/20`}
                />
              </div>

              <Button
                type="submit"
                disabled={sending}
                className={`w-full rounded-xl py-6 font-semibold
                  ${isDarkMode 
                    ? 'bg-[#00f2ea] hover:bg-[#00d4d4] text-black' 
                    : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                  }`}
              >
                {sending ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-current/20 border-t-current rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* FAQ Quick Links */}
        <div className="glass rounded-2xl p-8 mt-8">
          <h2 className="text-xl font-semibold mb-4">Common Questions</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <h3 className="font-medium mb-2">How accurate is the weather data?</h3>
              <p className="text-sm opacity-70">We use data from multiple reliable sources including OpenWeatherMap and NOAA to provide accurate forecasts.</p>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <h3 className="font-medium mb-2">Is my location data private?</h3>
              <p className="text-sm opacity-70">Yes! We only use your location to provide weather data and never sell your information. See our Privacy Policy.</p>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <h3 className="font-medium mb-2">Can I save multiple locations?</h3>
              <p className="text-sm opacity-70">Absolutely! You can save unlimited locations and quickly switch between them from the dashboard.</p>
            </div>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}>
              <h3 className="font-medium mb-2">How do I report a bug?</h3>
              <p className="text-sm opacity-70">Use the contact form above or email us directly at support@theforecasttracker.com.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
